// =====================================================================
// crmCustomer — Opportunities (kanban + list + detail with MEDDPICCR)
//
// This file holds the three page roots: OppKanban, OppList, OppDetail.
// Each opportunity DETAIL tab's editor is its own file so per-tab bugs
// and per-tab features can be worked on without scrolling through the
// whole opportunity page:
//
//   opportunities-stakeholders.jsx      Stakeholders tab
//   opportunities-commercials-tab.jsx   Metrics + Commission editors (Commercials tab)
//   opportunities-close-tab.jsx         Risk register (Close tab)
//   opportunities-meddpiccr-tab.jsx     Override dialog + DC / DP editors (MEDDPICCR tab)
//   opportunities-forecast-editor.jsx   Inline forecast-category editor (Overview tab)
//
// Pure helpers that don't depend on React live in their own .js files
// alongside (opportunity-commission.js, opportunity-meddpiccr.js,
// opportunity-thresholds.js). All of these must load BEFORE this file —
// see tpCustFe/build.js and tpCustFe/index.html for the script order.
// =====================================================================

function AdvanceStageDialog({ currentStage, onClose, onConfirm }) {
  const stages = D.STAGES || [];
  const currentIdx = stages.indexOf(currentStage);
  const nextStages = currentIdx >= 0 ? stages.slice(currentIdx + 1) : stages;
  const [selected, setSelected] = useState(nextStages[0] || "");
  if (!nextStages.length) return (
    <Dialog title="Advance stage" onClose={onClose} footer={<button className="btn btn-sm" onClick={onClose}>Close</button>}>
      <div className="muted">This opportunity is already at the final stage.</div>
    </Dialog>
  );
  return (
    <Dialog title="Advance stage" onClose={onClose} footer={<>
      <button className="btn btn-sm" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary btn-sm" onClick={() => { onConfirm(selected); onClose(); }}>Advance</button>
    </>}>
      <div className="form-field"><label>Move to stage</label>
        <select value={selected} onChange={e => setSelected(e.target.value)}>
          {nextStages.map(s => <option key={s}>{s}</option>)}
        </select>
      </div>
    </Dialog>
  );
}

// STAGE_FORECAST_MIN moved into settings.meddpiccThresholds per the P2.2
// consolidation. Read via window.tpThresholds.stageForecastMin(D.SETTINGS)
// at each render so admin edits to the thresholds take effect without a
// page reload. The previous hard-coded literal was:
//   { Pipeline: 0, "Best case": 4, Commit: 6, Closed: 7 }
// which is now the fallback inside opportunity-thresholds.js.

// Per-element tooltip explaining where the value comes from when the
// MEDDPICCR-tab toggle is disabled (i.e. the element is currently
// derived or bridged). Reviewer A1: a click that appears to do
// nothing destroys trust — the disabled toggle plus this tooltip is
// the documented behaviour. The Override button next to it stays
// active for genuine edge cases.
const MEDDPICCR_DERIVED_TOOLTIPS = {
  metrics:          "Set automatically when the Metric field is filled (Commercials tab).",
  economicBuyer:    "Set automatically from the Economic Buyer stakeholder. Edit on the Stakeholders tab.",
  decisionCriteria: "Set automatically when the Decision Criteria field is filled (MEDDPICCR tab).",
  decisionProcess:  "Set automatically when the Decision Process field is filled (MEDDPICCR tab).",
  paperProcess:     "Set automatically from the Commercials tab (legal / security / procurement / proposal / contract date).",
  identifyPain:     "Set from linked research.",  // unused — I keeps the working toggle
  champion:         "Set automatically from the Champion stakeholder. Edit on the Stakeholders tab.",
  competition:      "Set automatically from Competitors + competitive position (Close tab).",
  risk:             "Set automatically when the Risk register has every open row's owner + mitigation filled (Close tab).",
};

function OppKanban({ navigate }) {
  // Local copy supports optimistic drag updates. Re-sync from the global
  // store whenever DataProvider re-runs (after tpReload mutations).
  const [opps, setOpps] = useState(D.OPPORTUNITIES);
  useEffect(() => { setOpps(D.OPPORTUNITIES); }, [D.OPPORTUNITIES]);
  const [filterMotion, setFilterMotion] = useState("all");
  const [filterOwner, setFilterOwner] = useState("all");
  const [filterSegment, setFilterSegment] = useState("all");
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [collapsed, setCollapsed] = useState({});

  // The kanban is the active-pipeline board — closed (Won/Lost) stages are
  // terminal and excluded; closed deals live in the List view.
  const stages = D.STAGES.filter(s => !s.startsWith("Closed"));
  const visible = opps.filter(o => {
    const a = getAccount(o.accountId);
    if (o.stage && o.stage.startsWith("Closed")) return false;
    if (filterMotion !== "all" && o.motion !== filterMotion) return false;
    if (filterOwner !== "all" && o.owner !== filterOwner) return false;
    if (filterSegment !== "all" && a && a.segment !== filterSegment) return false;
    return true;
  });

  const onDrop = (stage) => {
    if (!dragId) { setDragOver(null); return; }
    const dropped = dragId;
    setDragId(null); setDragOver(null);
    const target = opps.find(o => o.id === dropped);
    if (!target || target.stage === stage) return;
    // Optimistic local update, then persist. On failure, revert.
    setOpps(prev => prev.map(o => o.id === dropped ? { ...o, stage, daysInStage: 0 } : o));
    tpAPI.opportunities.update(dropped, { stage, daysInStage: 0 }).catch(err => {
      console.error("Failed to advance stage:", err);
      setOpps(prev => prev.map(o => o.id === dropped ? target : o));
    });
  };

  return (
    <>
      <PageHead
        title="Opportunities"
        sub={`${visible.length} open · drag cards across stages to update`}
        actions={
          <>
            <button className="btn btn-sm" onClick={() => navigate("sales/opportunities/list")}>List view</button>
            <button className="btn btn-primary btn-sm" onClick={() => setShowNew(true)}>+ New opportunity</button>
          </>
        }
      />
      <div className="card">
        <FilterToolbar count={visible.length} total={opps.length}>
          <select value={filterMotion} onChange={e => setFilterMotion(e.target.value)}>
            <option value="all">All motions</option>
            <option>SME self-serve</option><option>SME assisted</option>
            <option>Mid-market</option><option>Enterprise</option>
          </select>
          <select value={filterOwner} onChange={e => setFilterOwner(e.target.value)}>
            <option value="all">All owners</option>
            {assignableReps("AE").map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <select value={filterSegment} onChange={e => setFilterSegment(e.target.value)}>
            <option value="all">All segments</option>
            <option>SME</option><option>Mid-market</option><option>Enterprise</option>
          </select>
          <span className="chip">MEDDPICCR pips shown on cards</span>
        </FilterToolbar>
        <div style={{ padding: 12 }}>
          <div className="kanban">
            {stages.map(stage => {
              const colOpps = visible.filter(o => o.stage === stage);
              const acv = colOpps.reduce((a, o) => a + o.acv, 0);
              const isCollapsed = !!collapsed[stage];
              return (
                <div key={stage} className={`kanban-col${isCollapsed ? " collapsed" : ""}`}>
                  <div className="kanban-col-head"
                       onClick={() => setCollapsed(c => ({ ...c, [stage]: !c[stage] }))}
                       title={isCollapsed ? "Expand column" : "Collapse column"}>
                    {isCollapsed ? (
                      <>
                        <div className="kc-count">{colOpps.length}</div>
                        <div className="kc-name-v">{stage}</div>
                      </>
                    ) : (
                      <>
                        <div className="kc-name">{stage}</div>
                        <div className="kc-count">{colOpps.length}</div>
                        <div className="kc-amt">{fmtMoney(acv)}</div>
                      </>
                    )}
                  </div>
                  {!isCollapsed && (
                  <div
                    className={`kanban-col-body${dragOver === stage ? " drop-target" : ""}`}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(stage); }}
                    onDragLeave={() => setDragOver(null)}
                    onDrop={() => onDrop(stage)}
                  >
                    {colOpps.map(o => {
                      const a = getAccount(o.accountId) || {};
                      const fc = o.forecastCategory;
                      const fcCls = fc === "Commit" ? "fc-commit" : fc === "Best case" ? "fc-bestcase" : "fc-pipeline";
                      const score = meddScore(o);
                      return (
                        <div key={o.id}
                             className={`kanban-card ${fcCls}${dragId === o.id ? " dragging" : ""}`}
                             draggable
                             onDragStart={(e) => { setDragId(o.id); e.dataTransfer.effectAllowed = "move"; }}
                             onDragEnd={() => { setDragId(null); setDragOver(null); }}
                             onClick={(e) => { if (!dragId) navigate("sales/opportunities/" + o.id); }}
                        >
                          <div className="kanban-card-name">{o.name}</div>
                          <div className="kanban-card-meta">
                            <span>{a.name} · <Badge tone="slate">{a.segment}</Badge></span>
                            <span className="numeric">{fmtMoney(o.acv, o.currency)}</span>
                          </div>
                          <div className="kanban-card-foot">
                            <ForecastBadge cat={fc} />
                            <span title={`MEDDPICCR ${score.got}/${score.total}`}><MeddPip opp={o} /></span>
                            <span className="muted" style={{ fontSize: 11, marginLeft: "auto" }}>{o.daysInStage}d</span>
                            <Avatar user={o.owner} size={20} />
                          </div>
                        </div>
                      );
                    })}
                    {colOpps.length === 0 && <div className="muted" style={{ fontSize: 12, textAlign: "center", padding: 16 }}>—</div>}
                  </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {showNew && <NewOpportunityDialog onClose={() => setShowNew(false)} navigate={navigate} />}
    </>
  );
}

function OppList({ navigate }) {
  return (
    <>
      <PageHead
        title="Opportunities — list"
        sub="Tabular alternative to kanban"
        actions={<button className="btn btn-sm" onClick={() => navigate("sales/opportunities")}>Kanban view</button>}
      />
      <div className="card">
        <table className="tbl tbl-flush">
          <thead><tr>
            <th>Name</th><th>Account</th><th>Segment</th><th>Motion</th><th>Stage</th>
            <th className="right">ACV</th><th>Close date</th><th>Forecast</th><th>MEDDPICCR</th><th>Owner</th><th className="right">Days in stage</th>
          </tr></thead>
          <tbody>
            {D.OPPORTUNITIES.map(o => {
              const a = getAccount(o.accountId) || {};
              const score = meddScore(o);
              return (
                <tr key={o.id} onClick={() => navigate("sales/opportunities/" + o.id)}>
                  <td><span className="tbl-link">{o.name}</span></td>
                  <td>{a.name}</td>
                  <td><Badge tone="slate">{a.segment}</Badge></td>
                  <td><span className="muted" style={{ fontSize: 12 }}>{o.motion}</span></td>
                  <td><StageBadge stage={o.stage} /></td>
                  <td className="right numeric">{fmtMoney(o.acv, o.currency)}</td>
                  <td>{fmtDateShort(o.closeDate)}{o.pushCount > 0 && <span className="muted"> ({o.pushCount}×)</span>}</td>
                  <td><ForecastBadge cat={o.forecastCategory} /></td>
                  <td><span className="numeric">{score.got}/{score.total}</span> <MeddPip opp={o} /></td>
                  <td><Avatar user={o.owner} size={20} /></td>
                  <td className="right numeric">{o.daysInStage}d</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// =====================================================================
// Opportunity Detail — the seven-tab deal page.
// Per-tab editors are imported via window globals (see file header).
// =====================================================================
function OppDetail({ id, navigate }) {
  const original = getOpp(id);
  const [opp, setOpp] = useState(original);
  const [tab, setTab] = useState("overview");
  const [action, setAction] = useState(null);

  if (!opp) return <PageHead title="Not found" sub={`No opportunity with id ${id}`} />;
  const a = getAccount(opp.accountId) || {};
  // Champion / Economic Buyer / Primary now come from the opportunity_contacts
  // junction — same contact can hold different roles in different deals.
  const champ = getOppContactByRole(opp.id, "Champion");
  const eb = getOppContactByRole(opp.id, "Economic Buyer");
  const primary = getOppPrimaryContact(opp.id);
  const score = meddScore(opp);
  // Same derived state the score ring is computed off — used so the row
  // "confirmed" styling reflects the actual qualification picture, not the
  // legacy opp.meddpicc toggle. Adding an EB stakeholder lights up the E
  // row on next render even though no one clicked anything.
  const derivedMedd = deriveOppState(opp);
  const [overrideKey, setOverrideKey] = useState(null);
  const stageMin = window.tpThresholds.stageForecastMin(D.SETTINGS);
  const minNeeded = stageMin[opp.forecastCategory] || 0;
  const meddKeys = D.MEDDPICCR_KEYS;

  // Account contacts plus their (optional) junction row for THIS deal.
  // Each entry: { contact, oc | null } — used for the Stakeholders panel.
  const accountContacts = D.CONTACTS.filter(c => c.accountId === opp.accountId);
  const stakeholders = accountContacts.map(c => ({
    contact: c,
    oc: getOppContactRole(opp.id, c.id),
  }));
  const onDealCount = stakeholders.filter(s => s.oc).length;
  const oppSurveys = D.SURVEYS.filter(s => s.opp === opp.id);
  const oppInterviews = D.INTERVIEWS.filter(i => i.opp === opp.id);
  const oppActivities = (D.ACTIVITIES || []).filter(act => act.oppId === opp.id);

  const toggleMedd = (k) => {
    const prev = opp;
    const meddpicc = { ...(opp.meddpicc || {}), [k]: !(opp.meddpicc && opp.meddpicc[k]) };
    const meddpiccUpdated = new Date().toISOString().slice(0, 10);
    setOpp(p => ({ ...p, meddpicc, meddpiccUpdated }));
    tpAPI.opportunities.update(opp.id, { meddpicc, meddpiccUpdated }).catch(err => {
      console.error("Failed to update MEDDPICCR:", err);
      setOpp(prev);
    });
  };

  const tabs = [
    { key: "overview",  label: "Overview" },
    { key: "meddpicc",  label: "MEDDPICCR", count: `${score.got}/${score.total}` },
    { key: "stakeholders", label: "Stakeholders", count: onDealCount },
    { key: "activity",  label: "Activity" },
    { key: "research",  label: "Research", count: oppSurveys.length + oppInterviews.length },
    { key: "commercials", label: "Commercials" },
    { key: "close",     label: "Close" },
  ];

  return (
    <>
      <PageHead
        title={opp.name}
        sub={
          <>
            <a className="tbl-link" onClick={() => navigate("sales/accounts/" + a.id)}>{a.name}</a>
            <span className="subtle"> · </span>{a.segment} · {opp.motion} · Created {fmtDate(opp.created)}
          </>
        }
        actions={
          <>
            <ResearchBadge surveys={oppSurveys.length} interviews={oppInterviews.length} />
            <button className="btn btn-sm" onClick={() => setAction("activity")}>Log activity</button>
            <button className="btn btn-primary btn-sm" onClick={() => setAction("advance")}>Advance stage</button>
          </>
        }
      />

      {/* Stage progress strip */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ paddingTop: 14, paddingBottom: 14 }}>
          <div className="flex-between" style={{ marginBottom: 8 }}>
            <div className="flex-gap" style={{ alignItems: "center" }}>
              <StageBadge stage={opp.stage} />
              <span className="muted" style={{ fontSize: 12 }}>{opp.daysInStage} days in stage</span>
              <span className="muted" style={{ fontSize: 12 }}>· Close {fmtDate(opp.closeDate)}{opp.pushCount > 0 && ` (pushed ${opp.pushCount}×)`}</span>
            </div>
            <div className="flex-gap" style={{ alignItems: "center" }}>
              <ForecastBadge cat={opp.forecastCategory} />
              <span className="muted" style={{ fontSize: 12 }}>{opp.winProb}% win</span>
              <span className="numeric" style={{ fontWeight: 600 }}>{fmtMoneyExact(opp.acv, opp.currency)} <span className="muted">ACV</span></span>
            </div>
          </div>
          <div className="stage-bar">
            {D.STAGES.filter(s => !s.startsWith("Closed") || s === "Closed Won").map(s => {
              const cur = opp.stage === s;
              const idx = D.STAGES.indexOf(s); const oppIdx = D.STAGES.indexOf(opp.stage);
              const done = idx < oppIdx;
              return <div key={s} className={`sb-step ${done ? "done" : cur ? "cur" : ""}`} title={s} />;
            })}
          </div>
          {score.got < minNeeded && (
            <div style={{ marginTop: 12, padding: "8px 12px", background: "var(--red-soft)", color: "var(--red)", borderRadius: 6, fontSize: 12.5 }}>
              ⚠ MEDDPICCR score ({score.got}/{score.total}) is below the {opp.forecastCategory} threshold ({minNeeded}). Manager review required.
              {opp.forecastOverride && opp.forecastOverride.note && (
                <div style={{ marginTop: 6, fontSize: 12, color: "var(--text)" }}>
                  <strong>Override note:</strong> {opp.forecastOverride.note}
                  <span className="muted"> — by {opp.forecastOverride.by} on {fmtDate(opp.forecastOverride.at)}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <Tabs tabs={tabs} current={tab} onChange={setTab} />

      <div className="detail">
        <div>
          {tab === "overview" && (
            <>
              <div className="card">
                <div className="card-head"><div className="card-title">Deal overview</div></div>
                <div className="card-body">
                  <div className="kv">
                    <div className="kv-label">Account</div><div className="kv-value"><a className="tbl-link" onClick={() => navigate("sales/accounts/" + a.id)}>{a.name}</a></div>
                    <div className="kv-label">Primary contact</div><div className="kv-value">{primary ? `${primary.first} ${primary.last} · ${primary.title}` : "—"}</div>
                    <div className="kv-label">Champion</div><div className="kv-value">{champ ? <><a className="tbl-link" onClick={() => navigate("sales/contacts/" + champ.id)}>{champ.first} {champ.last}</a> · <Badge tone="amber">Champion</Badge></> : <span className="muted">Not identified</span>}</div>
                    <div className="kv-label">Economic buyer</div><div className="kv-value">{eb ? <><a className="tbl-link" onClick={() => navigate("sales/contacts/" + eb.id)}>{eb.first} {eb.last}</a> · <Badge tone="amber">EB</Badge></> : <span className="muted">Not identified</span>}</div>
                    <div className="kv-label">Type</div><div className="kv-value">{opp.type}</div>
                    <div className="kv-label">Source</div><div className="kv-value">{opp.source}</div>
                    <div className="kv-label">Motion</div><div className="kv-value">{opp.motion}</div>
                    <div className="kv-label">ACV / TCV</div><div className="kv-value numeric">{fmtMoneyExact(opp.acv, opp.currency)} / {fmtMoneyExact(opp.tcv, opp.currency)}</div>
                    <div className="kv-label">Contract length</div><div className="kv-value">{opp.contractMonths} months</div>
                    <div className="kv-label">Discount</div><div className="kv-value">{opp.discount}% · <Badge tone={opp.discountStatus === "Approved" ? "green" : "slate"}>{opp.discountStatus}</Badge></div>
                    <div className="kv-label">Close date</div><div className="kv-value">{fmtDate(opp.closeDate)} · pushed {opp.pushCount}× (last {fmtDate(opp.closeChanged)})</div>
                    <div className="kv-label">Forecast</div>
                    <div className="kv-value" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <ForecastBadge cat={opp.forecastCategory} />
                      <span className="muted">· last changed {fmtDate(opp.forecastChanged)}</span>
                      <ForecastEditor opp={opp} derivedScoreGot={score.got} onChange={(next) => setOpp(next)} />
                    </div>
                    <div className="kv-label">Win probability</div><div className="kv-value">{opp.winProb}%{opp.winProbOverride != null && <> <span className="muted">(override → {opp.winProbOverride}%)</span></>}</div>
                    <div className="kv-label">Multi-threaded</div><div className="kv-value">{opp.multiThreaded ? <Badge tone="green">Yes</Badge> : <Badge tone="red">No — single-threaded risk</Badge>} · {opp.externalStakeholders} external stakeholders</div>
                    <div className="kv-label">Compelling event</div><div className="kv-value">{opp.compellingEvent ? <>{opp.compellingEventDesc} <span className="muted">(by {fmtDate(opp.compellingEventDate)})</span></> : <span className="muted">None identified</span>}</div>
                    <div className="kv-label">Discovery</div><div className="kv-value">{opp.discoveryDone ? <Badge tone="green">Completed</Badge> : <Badge tone="amber">Pending</Badge>}</div>
                    <div className="kv-label">Expansion flag</div><div className="kv-value">{opp.expansionFlag ? <Badge tone="amber">Likely 12-18mo expansion</Badge> : <span className="muted">No</span>}</div>
                    <div className="kv-label">Owner / SE / SDR</div><div className="kv-value">{getUser(opp.owner)?.name} · {getUser(opp.se)?.name || "—"} · {getUser(opp.sdr)?.name || "—"}</div>
                    <div className="kv-label">Next step</div><div className="kv-value">{opp.nextStep} <span className="muted">· due {fmtDate(opp.nextStepDue)}</span></div>
                  </div>
                </div>
              </div>

              <div className="card mt-16">
                <div className="card-head"><div className="card-title">Stage progression history</div></div>
                <div className="card-body" style={{ padding: 0 }}>
                  <table className="tbl tbl-flush">
                    <thead><tr><th>Stage</th><th>Entered</th><th className="right">Days</th></tr></thead>
                    <tbody>
                      {(opp.history || []).map((h, i) => {
                        const next = opp.history[i+1];
                        const days = next ? daysBetween(next.entered, h.entered) : daysBetween(new Date(), h.entered);
                        return (
                          <tr key={i} style={{ cursor: "default" }}>
                            <td><StageBadge stage={h.stage} /></td>
                            <td>{fmtDate(h.entered)}</td>
                            <td className="right numeric">{days}d</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {tab === "meddpicc" && (
          <>
            <div className="card">
              <div className="card-head">
                <div className="card-title">MEDDPICCR qualification</div>
                <div className="card-sub">Last updated {fmtDate(opp.meddpiccUpdated)} · toggle elements as you confirm them</div>
              </div>
              <div className="card-body">
                <div className="medd-summary">
                  <div className="medd-score-ring">
                    <svg viewBox="0 0 36 36">
                      <circle cx="18" cy="18" r="15.91" fill="none" strokeWidth="3" className="medd-score-ring-bg"/>
                      <circle cx="18" cy="18" r="15.91" fill="none" strokeWidth="3" strokeLinecap="round"
                              className="medd-score-ring-fg"
                              strokeDasharray={`${(score.got/score.total)*100}, 100`} />
                    </svg>
                    <div className="medd-score-num">{score.got}<small>/{score.total}</small></div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>MEDDPICCR completion</div>
                    <div className="muted" style={{ fontSize: 12.5 }}>
                      {score.got >= minNeeded
                        ? `Meets ${opp.forecastCategory} forecast threshold.`
                        : `Below ${opp.forecastCategory} threshold (${minNeeded} required).`}
                      {" "}Manager review {score.got < minNeeded ? "required" : "optional"}.
                    </div>
                  </div>
                  <Badge tone={score.got >= minNeeded ? "green" : "red"}>{score.got >= minNeeded ? "On track" : "Gating"}</Badge>
                </div>

                {meddKeys.map(k => {
                  const entry = derivedMedd[k.key] || { value: false, source: "derived" };
                  const overridden = entry.source && entry.source !== "derived";
                  // Reviewer A1 (see MEDDPICCR-reviewer-response.md): a click
                  // that appears to do nothing destroys trust in the score.
                  // The legacy toggle is disabled when the element's state
                  // is currently derived (or bridged). identifyPain is the
                  // single exception — it still legitimately rides the
                  // manual toggle until I gets a structured field.
                  const toggleEnabled = overridden || k.key === "identifyPain";
                  const toggleTip = toggleEnabled
                    ? `Toggle ${k.label}`
                    : MEDDPICCR_DERIVED_TOOLTIPS[k.key] || "Set automatically — use Override to force a value.";
                  return (
                    <div key={k.key} className={`medd-row${entry.value ? " confirmed" : ""}`}>
                      <div className="medd-letter">{k.short}</div>
                      <div>
                        <div className="medd-label">
                          {k.label}
                          {overridden && (
                            <Badge tone="amber" style={{ marginLeft: 6, fontSize: 10 }} title={`Override by ${entry.by} on ${fmtDate(entry.at)} — ${entry.reason}`}>
                              override
                            </Badge>
                          )}
                        </div>
                        <div className="medd-desc">{k.desc}</div>
                      </div>
                      <div className="flex-gap" style={{ gap: 6, alignItems: "center" }}>
                        <button className="btn btn-sm" onClick={() => setOverrideKey(k.key)} title="Force this element ON or OFF with an attributed reason">
                          {overridden ? "Edit override" : "Override"}
                        </button>
                        <button
                          className={`toggle${entry.value ? " on" : ""}`}
                          onClick={toggleEnabled ? () => toggleMedd(k.key) : undefined}
                          disabled={!toggleEnabled}
                          aria-label={toggleTip}
                          title={toggleTip}
                          style={!toggleEnabled ? { opacity: 0.4, cursor: "not-allowed" } : undefined}
                        />
                      </div>
                    </div>
                  );
                })}
                {overrideKey && (
                  <OverrideDialog
                    opp={opp}
                    elementKey={overrideKey}
                    elementLabel={(meddKeys.find(k => k.key === overrideKey) || {}).label || overrideKey}
                    derivedEntry={derivedMedd[overrideKey]}
                    onClose={() => setOverrideKey(null)}
                    onSaved={(next) => setOpp(next)}
                  />
                )}
              </div>
            </div>
            <DecisionCriteriaEditor opp={opp} onChange={(next) => setOpp(next)} />
            <DecisionProcessEditor  opp={opp} onChange={(next) => setOpp(next)} />
          </>
          )}

          {tab === "stakeholders" && (
            <StakeholderEditor opp={opp} stakeholders={stakeholders} accountName={a.name} navigate={navigate} />
          )}

          {tab === "activity" && (
            <div className="card">
              <div className="card-head"><div className="card-title">Activity</div><div className="card-sub">meetings · calls · emails tied to this deal</div></div>
              <ActivityTimeline activities={oppActivities} empty="No activity logged for this deal yet. Use “Log activity” to add one." />
            </div>
          )}

          {tab === "research" && (
            <div className="card">
              <div className="card-head"><div className="card-title">Research linked to this deal</div><div className="card-sub">Validate champion strength & EB alignment from documented research, not memory</div></div>
              <div className="card-body" style={{ padding: 0 }}>
                <table className="tbl tbl-flush">
                  <thead><tr><th>Type</th><th>Respondent</th><th>Role</th><th>Pain</th><th>ICP fit</th><th>Key finding</th><th>Date</th></tr></thead>
                  <tbody>
                    {oppSurveys.map(s => {
                      const c = getContact(s.contactId) || {};
                      const oc = getOppContactRole(opp.id, s.contactId);
                      return (
                        <tr key={s.id} onClick={() => navigate("research/surveys/" + s.id)}>
                          <td><Badge tone="blue">Survey</Badge> {s.type}</td>
                          <td><span className="tbl-link">{c.first} {c.last}</span></td>
                          <td><span className="muted">{oc ? oc.role : "—"}</span></td>
                          <td>{(s.pain || []).map(p => <Badge key={p} tone="slate">{p}</Badge>)}</td>
                          <td>{s.icpFit != null ? <Donut value={s.icpFit} size={28}/> : <span className="muted">—</span>}</td>
                          <td className="muted" style={{ maxWidth: 280 }}>{s.keyQuote ? `"${s.keyQuote}"` : "—"}</td>
                          <td>{fmtDateShort(s.completed || s.sent)}</td>
                        </tr>
                      );
                    })}
                    {oppInterviews.map(i => {
                      const c = getContact(i.contactId) || {};
                      const oc = getOppContactRole(opp.id, i.contactId);
                      return (
                        <tr key={i.id} onClick={() => navigate("research/interviews/" + i.id)}>
                          <td><Badge tone="amber">Interview</Badge> {i.type}</td>
                          <td><span className="tbl-link">{c.first} {c.last}</span></td>
                          <td><span className="muted">{oc ? oc.role : "—"}</span></td>
                          <td>{(i.pain || []).map(p => <Badge key={p} tone="slate">{p}</Badge>)}</td>
                          <td>{i.icpFit != null ? <Donut value={i.icpFit} size={28}/> : <span className="muted">—</span>}</td>
                          <td className="muted" style={{ maxWidth: 280 }}>{i.keyQuote ? `"${i.keyQuote}"` : "—"}</td>
                          <td>{fmtDateShort(i.date)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === "commercials" && (
            <>
              <div className="card">
                <div className="card-head"><div className="card-title">Commercials</div></div>
                <div className="card-body">
                  <div className="kv">
                    <div className="kv-label">Products / SKUs</div><div className="kv-value">{(opp.products || []).length ? opp.products.map(p => <Badge key={p} tone="slate">{p}</Badge>) : <span className="muted">—</span>}</div>
                    <div className="kv-label">Quote</div><div className="kv-value">{opp.quoteId ? <a className="tbl-link" onClick={()=>navigate("sales/quotes")}>{opp.quoteId}</a> : <span className="muted">No quote</span>}</div>
                    <div className="kv-label">Discount</div><div className="kv-value">{opp.discount}% · <Badge tone={opp.discountStatus === "Approved" ? "green" : "slate"}>{opp.discountStatus}</Badge>{opp.discountBy && <span className="muted"> by {getUser(opp.discountBy)?.name}</span>}</div>
                    <div className="kv-label">Legal review</div><div className="kv-value">{opp.legalReview ? <Badge tone="amber">Required</Badge> : <Badge tone="slate">Not required</Badge>}</div>
                    <div className="kv-label">Security review</div><div className="kv-value">{opp.securityReview ? <Badge tone="amber">Required</Badge> : <Badge tone="slate">Not required</Badge>}</div>
                    <div className="kv-label">Procurement</div><div className="kv-value">{opp.procurementInvolved ? <>Involved · est. {opp.procurementDays} days</> : <span className="muted">Not involved</span>}</div>
                    <div className="kv-label">Proposal</div><div className="kv-value">{opp.proposalSent ? <>Sent {fmtDate(opp.proposalSent)}{opp.proposalLink && <> · <a className="tbl-link" href={opp.proposalLink} target="_blank" rel="noopener noreferrer">view</a></>}</> : <span className="muted">Not sent</span>}</div>
                    <div className="kv-label">Contract dates</div><div className="kv-value">Start {fmtDateShort(opp.contractStart)} · sent {fmtDateShort(opp.contractSent)} · signed {fmtDateShort(opp.contractSigned)}</div>
                    <div className="kv-label">Order ID</div><div className="kv-value-mono">{opp.orderId || "—"}</div>
                  </div>
                </div>
              </div>
              <MetricsEditor opp={opp} onChange={(next) => setOpp(next)} />
              <CommissionEditor opp={opp} onChange={(next) => setOpp(next)} />
            </>
          )}

          {tab === "close" && (
            <>
              <div className="card">
                <div className="card-head"><div className="card-title">Close — win/loss capture</div></div>
                <div className="card-body">
                  <div className="kv">
                    <div className="kv-label">Compelling event</div>
                    <div className="kv-value">{opp.compellingEvent ? <>{opp.compellingEventDesc} · <b>{fmtDate(opp.compellingEventDate)}</b></> : <span className="muted">None identified</span>}</div>
                    <div className="kv-label">Competitors</div>
                    <div className="kv-value">{(opp.competitors || []).length ? opp.competitors.map(c => <Badge key={c} tone="slate">{c}</Badge>) : <span className="muted">None</span>}</div>
                    <div className="kv-label">Competitive position</div>
                    <div className="kv-value"><Badge tone={opp.competitivePosition === "Leading" ? "green" : opp.competitivePosition === "Behind" ? "red" : "amber"}>{opp.competitivePosition}</Badge></div>
                    <div className="kv-label">Loss reason</div><div className="kv-value">{opp.lossReason || <span className="muted">—</span>}</div>
                    <div className="kv-label">Loss detail</div><div className="kv-value muted">{opp.lossDetail || "—"}</div>
                    <div className="kv-label">Win reason</div><div className="kv-value">{opp.winReason || <span className="muted">—</span>}</div>
                  </div>
                </div>
              </div>
              <RiskRegisterEditor opp={opp} stakeholders={(D.OPPORTUNITY_CONTACTS || []).filter(oc => oc.oppId === opp.id)} />
            </>
          )}
        </div>

        <RightRail record={opp} link={{ oppId: opp.id, accountId: opp.accountId }} />
      </div>
      {action === "activity" && <QuickActionDialog type="activity" link={{ oppId: opp.id, accountId: opp.accountId }} onClose={() => setAction(null)} onSave={() => setAction(null)} />}
      {action === "advance" && <AdvanceStageDialog currentStage={opp.stage} onClose={() => setAction(null)} onConfirm={s => {
        const prev = opp;
        setOpp(p => ({ ...p, stage: s, daysInStage: 0 }));
        tpAPI.opportunities.update(opp.id, { stage: s, daysInStage: 0 }).catch(err => {
          console.error("Failed to update stage:", err);
          setOpp(prev);
        });
      }} />}
    </>
  );
}

window.OppKanban = OppKanban;
window.OppList = OppList;
window.OppDetail = OppDetail;
window.AdvanceStageDialog = AdvanceStageDialog;
